/**
 * useTheme — manual light/dark theme switch.
 *
 * - Persists choice in localStorage (`tl-theme`).
 * - Sets <html data-theme="light|dark"> so tokens.css can override.
 * - When the user has not chosen, defaults to light mode.
 * - Exposes `isDark` so callers (e.g. App.vue's Naive UI provider)
 *   can react to changes.
 *
 * The store is a singleton ref shared across all components — call
 * useTheme() from anywhere and you get the same state.
 */
import { computed, ref, watch } from "vue";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "tl-theme";
const stored = readStored();
const mode = ref<ThemeMode | "system">(stored ?? "light");

function readStored(): ThemeMode | "system" | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return null;
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolveDark(mode: ThemeMode | "system"): boolean {
  return mode === "dark" || (mode === "system" && systemPrefersDark());
}

function applyHtmlAttr(isDark: boolean) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const next: ThemeMode = isDark ? "dark" : "light";
  html.setAttribute("data-theme", next);
  // Keep the theme-color meta tag in sync so the browser chrome
  // (mobile address bar) matches the app background.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = isDark ? "#141414" : "#f8f5ee";
  }
}

// Bootstrap once on module load so the very first paint is correct.
applyHtmlAttr(resolveDark(mode.value));

// React to system changes only when the user has not chosen explicitly.
if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", (e) => {
      if (mode.value !== "system") return;
      applyHtmlAttr(e.matches);
    });
}

const isDark = computed<boolean>(() => resolveDark(mode.value));

watch(
  mode,
  (next) => {
    applyHtmlAttr(resolveDark(next));
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  },
  { immediate: true },
);

export function useTheme() {
  function setTheme(next: ThemeMode | "system") {
    mode.value = next;
  }

  function toggle() {
    mode.value = isDark.value ? "light" : "dark";
  }

  return {
    /** Current effective resolved darkness (read-only computed). */
    isDark,
    /** Current mode: "light" | "dark" | "system" (read-only ref). */
    mode,
    setTheme,
    toggle,
  };
}
